/**
 * Discount engine — zod schemas for the discount type/segment enums and the three
 * engine tables (discount_schedules / customer_discounts / discount_audit_log).
 *
 * Row schemas mirror the DDL in supabase/migrations/20260626000001_discount_engine.sql
 * exactly (column names, nullability, numeric-as-string for NUMERIC columns).
 *
 * NUMERIC columns arrive over PostgREST as JSON strings — they are typed `z.string()`
 * here (decimal-string), never `z.number()`, to preserve money precision (decimal.js).
 * Duration columns (active_duration, inactive_duration) are INTEGER (plain seconds)
 * in the DB — typed `z.number().int()` here; no INTERVAL string parsing needed.
 *
 * Source: 2026-06-04-wandero-discount-engine spec §4.1 / §5.
 */

import { z } from 'zod';
import { Decimal } from 'decimal.js';

// ── Shared validators ─────────────────────────────────────────────────────────

/**
 * Non-negative decimal-string refinement for `discount_value` (NUMERIC-as-string).
 *
 * Defense-in-depth: a negative discount_value flips a discount into a surcharge
 * (over-charge). This is the single reusable choke point — apply to every schema
 * that reads or writes `discount_value` (row schemas here + any API contract that
 * represents a write or resolver input). Uses decimal.js (not parseFloat) to stay
 * consistent with money handling elsewhere in the codebase.
 *
 * Boundary "0" / "0.00" is accepted (zero discount is valid, just a no-op).
 * Rejects: non-numeric strings ("abc", "1.2.3", ""), NaN, non-finite, negatives.
 *
 * Source: 2026-07-04-wandero-discount-value-nonneg-schema.
 */
export const nonNegativeDecimalString = z.string().refine(
  (v) => {
    let d: Decimal;
    try {
      d = new Decimal(v);
    } catch {
      return false;
    }
    return d.isFinite() && !d.isNegative();
  },
  { message: 'discount_value must be a non-negative decimal string' },
);

// ── Enums (single source of truth for both sources) ──────────────────────────

/** Discount value interpretation: 'pct' = percent off, 'amount' = absolute off. */
export const DISCOUNT_TYPES = ['pct', 'amount'] as const;
export const DiscountType = z.enum(DISCOUNT_TYPES);
export type DiscountType = z.infer<typeof DiscountType>;

/** Customer segment driving segment-based rules. */
export const CUSTOMER_SEGMENTS = ['new', 'returning'] as const;
export const CustomerSegment = z.enum(CUSTOMER_SEGMENTS);
export type CustomerSegment = z.infer<typeof CustomerSegment>;

/** Audit-log event kinds. */
export const DISCOUNT_AUDIT_EVENTS = ['enabled', 'disabled', 'applied'] as const;
export const DiscountAuditEvent = z.enum(DISCOUNT_AUDIT_EVENTS);
export type DiscountAuditEvent = z.infer<typeof DiscountAuditEvent>;

/** Which trigger type produced a candidate / audit row. */
export const DISCOUNT_SOURCES = ['schedule', 'segment'] as const;
export const DiscountSource = z.enum(DISCOUNT_SOURCES);
export type DiscountSource = z.infer<typeof DiscountSource>;

// ── discount_schedules row ───────────────────────────────────────────────────

export const DiscountScheduleRowSchema = z.object({
  id: z.number().int(),
  site_code: z.string(),
  // TIMESTAMPTZ — ISO string from PostgREST.
  start_at: z.string(),
  // INTEGER (seconds) — active ON window in seconds; must be > 0.
  active_duration: z.number().int().positive(),
  // INTEGER (seconds) — pause window in seconds; 0 = permanently ON after start.
  inactive_duration: z.number().int().nonnegative(),
  // NULL = infinite repeat.
  end_at: z.string().nullable(),
  discount_type: DiscountType,
  // NUMERIC(10,2) as decimal-string. Non-negative (a negative value would flip
  // the discount into a surcharge) — see `nonNegativeDecimalString` above.
  discount_value: nonNegativeDecimalString,
  enabled: z.boolean(),
  note: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type DiscountScheduleRow = z.infer<typeof DiscountScheduleRowSchema>;

// ── customer_discounts row ───────────────────────────────────────────────────

export const CustomerDiscountRowSchema = z.object({
  id: z.number().int(),
  site_code: z.string(),
  segment: CustomerSegment,
  discount_type: DiscountType,
  discount_value: nonNegativeDecimalString,
  min_paid_orders: z.number().int().nonnegative(),
  enabled: z.boolean(),
  note: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type CustomerDiscountRow = z.infer<typeof CustomerDiscountRowSchema>;

// ── discount_audit_log row ───────────────────────────────────────────────────

export const DiscountAuditLogRowSchema = z.object({
  id: z.number().int(),
  occurred_at: z.string(),
  event: DiscountAuditEvent,
  source: DiscountSource.nullable(),
  schedule_id: z.number().int().nullable(),
  segment_id: z.number().int().nullable(),
  site_code: z.string().nullable(),
  segment: z.string().nullable(),
  client_token: z.string().uuid().nullable(),
  amanager_order_id: z.string().nullable(),
  base_value: z.string().nullable(),
  final_value: z.string().nullable(),
  discount_type: z.string().nullable(),
  // Constrain when present, allow null (nullable audit-log column).
  discount_value: nonNegativeDecimalString.nullable(),
  meta: z.record(z.string(), z.unknown()),
});
export type DiscountAuditLogRow = z.infer<typeof DiscountAuditLogRowSchema>;
